import { watch, type MaybeRefOrGetter, toValue } from "vue";
import { useRouter } from "vue-router";

/**
 * Sentinel paths handled by the router guard to render standard error pages.
 */
export const NOT_FOUND_REDIRECT_PATH = "/not-found";
export const UNAUTHORIZED_REDIRECT_PATH = "/unauthorized";

export function getHttpErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;

  const status =
    "status" in error
      ? (error as { status?: unknown }).status
      : "response" in error
        ? (error as { response?: { status?: unknown } }).response?.status
        : undefined;

  return typeof status === "number" ? status : undefined;
}

export function getQueryErrorRedirectPath(error: unknown): string | undefined {
  const status = getHttpErrorStatus(error);
  if (status === 403) return UNAUTHORIZED_REDIRECT_PATH;
  if (status === 404) return NOT_FOUND_REDIRECT_PATH;
  return undefined;
}

type TUseNotFoundRedirectOptions = {
  enabled?: MaybeRefOrGetter<boolean>;
};

/**
 * Redirects to the matching router error page when an object fetch fails with a
 * known access/not-found status. Prefer `redirectOnNotFound: true` on
 * `useGenericQuery` so snackbar suppression stays wired together.
 */
export function useNotFoundRedirect(
  errors: MaybeRefOrGetter<unknown> | readonly MaybeRefOrGetter<unknown>[],
  options?: TUseNotFoundRedirectOptions
) {
  const router = useRouter();
  const errorRefs = Array.isArray(errors) ? errors : [errors];

  watch(
    () => ({
      nextErrors: errorRefs.map((error) => toValue(error)),
      enabled: toValue(options?.enabled) ?? true,
    }),
    ({ nextErrors, enabled }) => {
      if (!enabled) return;

      const path = nextErrors.map(getQueryErrorRedirectPath).find(Boolean);
      if (path) void router.replace(path);
    }
  );
}

type TUseAccessCheckRedirectOptions = {
  enabled?: MaybeRefOrGetter<boolean>;
};

/**
 * Redirects to the unauthorized page when fetched data fails a caller-provided
 * predicate. Prefer `accessCheck` on `useGenericQuery`.
 *
 * `check` is a plain function (not a getter) so Vue does not treat it as
 * `MaybeRefOrGetter` and invoke it during unwrap.
 *
 * @param data - Reactive query data to inspect.
 * @param check - Returns `true` when access is allowed; `false` triggers a 403 redirect.
 */
export function useAccessCheckRedirect<T>(
  data: MaybeRefOrGetter<T | undefined | null>,
  check: ((data: T) => boolean) | undefined | null,
  options?: TUseAccessCheckRedirectOptions
) {
  const router = useRouter();

  watch(
    () => ({
      nextData: toValue(data),
      enabled: toValue(options?.enabled) ?? true,
    }),
    ({ nextData, enabled }) => {
      if (!enabled || !check || nextData == null) return;
      if (!check(nextData)) void router.replace(UNAUTHORIZED_REDIRECT_PATH);
    },
    { immediate: true }
  );
}
